import React, { useEffect, useState } from 'react';
import PeppolDataTable from '../../components/licence/peppol/PeppolDataTable';
import SftGlobalData from '../../library/SftGlobalData';
import axios from 'axios';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { faPlay  } from '@fortawesome/free-solid-svg-icons';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import UpdateIcon from '@mui/icons-material/Update';
import Colors from '../../library/Colors';
import FloatingSyncButton from '../../components/FloatingSyncButton';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Button, Checkbox, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeppolRegistrationForm from '../../components/licence/peppol/PeppolRegistrationForm';
import crypto from 'crypto-js';

const PeppolOverview = (props) => {
const [loading, setLoading] = useState(true);
const [corpList, setCorpList] = useState([]);
const [fetchedCorpList, setFetchedCorpList] = useState([]);
const [showForm, setShowForm] = useState(false);
const [selectedCompany, setSelectedCompany] = useState(null);
const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
const [companyToDelete, setCompanyToDelete] = useState(null);
const [patchDialogOpen, setPatchDialogOpen] = useState(false);
const [companyToPatch, setCompanyToPatch] = useState(null);
const [supplierData, setSupplierData] = useState({}); // Map of peppol_supplier_id -> supplier data
const [invoiceErrors, setInvoiceErrors] = useState({}); // Map of corp_id -> { corp, errors }
const [loadingErrors, setLoadingErrors] = useState(false);
const [selectedErrorKeys, setSelectedErrorKeys] = useState(new Set()); // Set of unique error keys selected for bulk reopen
const [resendingErrors, setResendingErrors] = useState(false);

const iconWrapper = (icon) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%', 
        width: '100%' 
    }}>
        {icon}
    </Box>
);

// Define columns specific to this overview page
const customColumns = [
    {
        field: 'id',
        headerName: "ID",
        width: 70,
        align: "left",
    },
    {
        field: 'peppol_status',
        headerName: props.t('status'),
        headerAlign: 'center',
        minWidth: 120,
        align: "center",
        valueGetter: (params) => {
            return params?.row?.peppol_supplier_id ? true : false;
        },
        renderCell: (params) => {
            // Check if the company is registered in Peppol and has given consent
            const hasSupplierId = !!params?.row?.peppol_supplier_id;
            const hasConsent = params?.row?.peppol_consent === "1";
            const isPeppol = hasSupplierId && hasConsent;

            // Generate tooltip message for missing conditions
            let tooltipMessage = '';
            if (!isPeppol) {
                const missingItems = [];
                if (!hasSupplierId) missingItems.push('Peppol Supplier ID');
                if (!hasConsent) missingItems.push('Consent');
                tooltipMessage = `Missing: ${missingItems.join(' and ')}`;
            }

            return (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    width: '100%', 
                    height: '100%' 
                }}>
                    {isPeppol ? (
                        <CheckCircleIcon style={{ color: '#228B22', fontSize: '24px' }} />
                    ) : (
                        <Tooltip title={tooltipMessage} arrow>
                            <CancelIcon style={{ color: '#FF0000', fontSize: '24px' }} />
                        </Tooltip>
                    )}
                </div>
            );
        },
    },
    {
        field: 'name',
        headerName: props.t('org_name'),
        minWidth: 70,
        align: "left",
        flex: 1,
    },
    {
        field: 'vat',
        headerName: props.t('vat'),
        minWidth: 150,
        align: "left",
        flex: 1,
    },
    {
        field: 'city',
        headerName: props.t('city'),
        minWidth: 120,
        align: "left",
        flex: 1,
    },
    {
        field: 'country',
        headerName: props.t('country'),
        minWidth: 120,
        align: "left",
        flex: 1,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: props.t('actions'),
        width: 180,
        align: "center",
        getActions: (params) => {
            const row = corpList.find(r => r.id === params.row.id);
            if (!row) return [];

            const actions = [];
            if (row.peppol_supplier_id) {
                // Check if current name is missing from supplier's registered names
                const supplier = supplierData[row.peppol_supplier_id];
                const supplierNames = supplier?.attributes?.names || [];
                const currentNameInSupplier = supplierNames.some(
                    nameObj => nameObj.value?.toLowerCase().trim() === row.name?.toLowerCase().trim()
                );

                // Show update name button if name is not in supplier's names
                if (supplier && !currentNameInSupplier) {
                    actions.push(
                        <GridActionsCellItem
                            key={`add-name-${row.id}`}
                            icon={iconWrapper(<UpdateIcon sx={{ color: Colors.sft_orange || '#ff9800' }} />)}
                            label="Add Name"
                            title={props.t('peppol_add_name_tooltip') || 'Current name not registered in Peppol'}
                            onClick={() => handleAddNameClick(row, supplier)}
                        />
                    );
                }

                if(props.loginObj.sftUserId !== "undefined" && props.loginObj?.sft_status != "0"){
                    actions.push(
                        <GridActionsCellItem
                            key={`sync-${row.id}`}
                            icon={iconWrapper(<FontAwesomeIcon icon={faPlay} color={Colors.def_green} size="2x" />)}
                            label="Sync"
                            onClick={() => { if(SftGlobalData.confirmWindow(props.t('peppol_corp_sync_confirm'))){startPeppolSync(params.row.id)} }}
                        />
                    );
                }

                actions.push(
                    <GridActionsCellItem
                        key={`patch-${row.id}`}
                        icon={iconWrapper(<BuildIcon sx={{ color: Colors.sft_blue }} />)}
                        label="Patch"
                        onClick={() => handlePatchClick(row)}
                    />,
                    <GridActionsCellItem
                        key={`delete-${row.id}`}
                        icon={iconWrapper(<DeleteIcon sx={{ color: Colors.error_red || '#f44336' }} />)}
                        label="Delete"
                        onClick={() => handleDeleteClick(row)}
                    />
                );
            } else {
                actions.push(
                    <GridActionsCellItem
                        key={`subscribe-${row.id}`}
                        icon={iconWrapper(<AddCircleOutlineIcon sx={{ color: Colors.sft_blue }} />)}
                        label="Subscribe"
                        onClick={() => handleSubscribe(row)}
                    />
                );
            }

            return actions;
        }
    }
];

// Custom row mapping for overview
const customRowMapper = (row) => ({
    id: row.id,
    name: row.name,
    vat: row.vat,
    city: row.city,
    country: row.country,
    peppol_id: row.peppol_id,
    peppol_scheme: row.peppol_scheme,
    peppol_supplier_id: row.peppol_supplier_id,
    peppol_consent: row.peppol_consent,
    // Don't include an actions field here
});

// We no longer need the separate actionComponents array since we've added
// the actions column directly in the customColumns array
const getCustomActions = [];

// Handle form submission for updates
const handleUpdateSubmit = (formData) => {
    
    // Call patchPeppolSupplier with the updated form data
    patchPeppolSupplier({
        ...selectedCompany,
        name: formData.corpname,
        city: formData.city,
        country: formData.countryCorp,
        homepage: formData.homepage,
        phoneNumber: formData.phoneNumber,
        address1: formData.street + ' ' + formData.streetNr,
        zip: formData.zip,
        email: formData.email,
        iban: formData.iban
    });
    
    // Return to list view
    handleBackToList();
};

// Handler for the Delete button click
const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
};

// Handler for the Patch button click
const handlePatchClick = (company) => {
    setCompanyToPatch(company);
    setPatchDialogOpen(true);
};

// Handler for the Add Name button click - adds current name to supplier's names
const handleAddNameClick = (company, supplier) => {
    if (!company || !supplier) return;

    const currentNames = supplier.attributes?.names || [];
    const newName = { value: company.name.trim() };

    // Build the patch request with the new name added
    const url = 'https://peppol.softtouch.eu/patchSupplierName';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);

    const postBody = {
        password: hash,
        id: company.peppol_supplier_id,
        names: [...currentNames, newName]
    };

    setLoading(true);
    axios.patch(url, postBody, {
        headers: { 'Content-Type': 'application/json' }
    }).then(response => {
        if (SftGlobalData.debug) {
            console.log('Add name response:', response.data);
        }
        props.notify(props.t('peppol_name_added') || 'Name added successfully', 7500, "top-right", props.theme, 1);
        // Refresh supplier data to update the UI
        fetchData();
    }).catch(error => {
        if (SftGlobalData.debug) {
            console.log('Error adding name:', error);
        }
        props.notify(props.t('errorOccured') + ': ' + (error.response?.data?.message || error.message), 7500, "top-right", props.theme, 1);
    }).finally(() => {
        setLoading(false);
    });
};

// Handler for confirming deletion
const handleDeleteConfirm = () => {
    if (!companyToDelete) return;
    
    // Start API call to delete the supplier
    deletePeppolSupplier(companyToDelete);
    setDeleteDialogOpen(false);
};

const startPeppolSync = (corpId = null) => {
    let url = SftGlobalData.baseURL_PEPPOL_API + 'sync';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);
    
    let postBody = {
        "password": hash,
        "clientId": props.db.client_id,
        "dbId": props.db.db_id
    };

    if(corpId) postBody.overwriteCorpId = corpId;

    if (!props.db?.client_id || !props.db?.db_id) {
        console.error('Cannot sync: no database selected');
        props.notify('Something went wrong with credentials, please reload the page.', 7500, "top-right", props.theme, 1);
        return;
    }
    
    axios.post(url, postBody, {
            headers: {
                'Accept': 'text/html'
            }        
        }).then(async response => {
            if (SftGlobalData.debug) {
                console.log('url', url);
                console.log('response', response);
            }

            // Open new tab and write the HTML directly from the response
            const newTab = window.open('', '_blank');
            newTab.document.write(response.data); // This is the HTML from your backend
            newTab.document.close();
            
            // Show notification in current page
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(response.data, 'text/html');
            const message = htmlDoc.querySelector('.success h2')?.textContent || "Sync started";
            props.notify(message, 7500, "top-right", props.theme, 1);
            
        }).catch(error => {
            if(SftGlobalData.debug) console.log('error', error);
             props.notify(props.t('errorOccured'), 7500, "top-right", props.theme, 1);
        })
}

// Trigger the Peppol backsync (checks/updates the status of outbound documents).
// Mirrors startPeppolSync but hits the checkOutboundStatus endpoint, which returns JSON.
const startPeppolBacksync = () => {
    let url = SftGlobalData.baseURL_PEPPOL_API + 'checkOutboundStatus';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);

    if (!props.db?.client_id || !props.db?.db_id) {
        console.error('Cannot backsync: no database selected');
        props.notify('Something went wrong with credentials, please reload the page.', 7500, "top-right", props.theme, 1);
        return;
    }

    let postBody = {
        "password": hash,
        "clientId": props.db.client_id,
        "dbId": props.db.db_id
    };

    axios.post(url, postBody, {
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(response => {
            if (SftGlobalData.debug) {
                console.log('url', url);
                console.log('response', response);
            }
            const message = response.data?.message || "Backsync started";
            props.notify(message, 7500, "top-right", props.theme, 1);
        }).catch(error => {
            if(SftGlobalData.debug) console.log('error', error);
            props.notify(props.t('errorOccured'), 7500, "top-right", props.theme, 1);
        });
}

// Handler for confirming patch
const handlePatchConfirm = () => {
    if (!companyToPatch) return;

    let url = 'https://peppol.softtouch.eu/getSupplier';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);
    
    let postBody = {
        "password": hash,
        "id": companyToPatch.peppol_supplier_id
    };
    
    axios.post(url, postBody, {
            headers: {
                'Content-Type': 'application/json'
            }        
        }).then(async response => {
            if (SftGlobalData.debug) {
                console.log('url', url);
                console.log('response', response);
            }
            let supplier = response.data;
            let supplier_id = supplier.id;
            let vat = supplier.attributes.enterpriseIdentification.vatNumber;
            let name = supplier.attributes.names[0].value;
            let iban = supplier.attributes.ibans[0].value;
            let address1 = supplier.attributes.street + ' ' + supplier.attributes.streetNumber;
            let zip = supplier.attributes.zip;
            let city = supplier.attributes.city;
            let country = supplier.attributes.country;
            let homepage = supplier.attributes.homepage;
            let phoneNumber = supplier.attributes.phoneNumber;
            let email = supplier.attributes.email;

            let peppolCompany = {
                id: companyToPatch.id,
                peppol_supplier_id: supplier_id,
                vat: vat,
                name: name,
                iban: iban,
                address1: address1,
                zip: zip,
                city: city,
                country: country,
                homepage: homepage,
                phoneNumber: phoneNumber,
                email: email
            };

            setSelectedCompany({
                ...peppolCompany,
                isEditing: true // Flag to indicate we're editing not creating
            });
            setShowForm(true);
            setPatchDialogOpen(false);
            
        })


};

// API function to delete a Peppol supplier
const deletePeppolSupplier = (company) => {
    setLoading(true);
    
    // Build the request to delete the supplier
    let url = 'https://peppol.softtouch.eu/deleteSupplier';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);
    
    let postBody = {
        "password": hash,
        "id": company.peppol_supplier_id
    };
    
    axios.post(url, postBody, {
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(async response => {
        if (SftGlobalData.debug) {
            console.log('url', url);
            console.log('data', postBody);
            console.log('response', response.data);
        }
        
        // Update the local data to reflect deletion
        const updatedCorpList = corpList.map(row => {
            if (row.id === company.id) {
                return {
                    ...row,
                    peppol_supplier_id: null,
                    updated: true
                };
            }
            return row;
        });
        
        // Update state and save to backend
        setCorpList(updatedCorpList);
        savePeppolInfoForCompany({
            id: company.id,
            peppol_supplier_id: "",
            peppol_consent: false
        });
        
    }).catch(error => {
        if (SftGlobalData.debug) {
            console.log('url', url);
            console.log('data', postBody);
            console.log('error', error);
        }
        props.notifyError('Error deleting supplier: ' + (error.response?.data?.message || error.message));
    }).finally(() => {
        setLoading(false);
    });
};

// API function to patch a Peppol supplier
const patchPeppolSupplier = (company) => {
    setLoading(true);
    
    // Build the request to patch the supplier
    let url = 'https://peppol.softtouch.eu/patchSupplier';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);
    
    // Prepare the fields that can be updated
    let postBody = {
        "password": hash,
        "id": company.peppol_supplier_id,
        "name": company.name.trim(),
        "city": company.city.trim(),
        "country": company.country.trim(),
        "homepage": company.homepage.trim(),
        "phoneNumber": company.phoneNumber.trim(),
        "street": company.street.trim() || (company.address1 ? company.address1.split(' ').slice(0, -1).join(' ').trim() : ''),
        "streetNumber": company.streetNr || (company.address1 ? company.address1.split(' ').pop() : ''),
        "zip": company.zip.trim(),
        "email": company.email.trim(),
        // Only include IBAN if it exists
        "ibans": company.iban ? [{ "value": company.iban }] : undefined
    };
    
    // Remove any undefined fields
    Object.keys(postBody).forEach(key => {
        if (postBody[key] === undefined) {
            delete postBody[key];
        }
    });
    
    console.log('Patch supplier request:', postBody);
    
    axios.patch(url, postBody, {
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(async response => {
        if (SftGlobalData.debug) {
            console.log('url', url);
            console.log('data', postBody);
            console.log('response', response.data);
        }

        // Update the local database as well
        savePeppolInfoForCompany( {
            ...company
        });

        // Notify user
       props.notify('Supplier updated', 7500, "top-right", props.theme, 1); 
        
    }).catch(error => {
        if (SftGlobalData.debug) {
            console.log('url', url);
            console.log('data', postBody);
            console.log('error', error);
        }
        props.notify('Error updating supplier: ' + (error.response?.data?.message || error.message), 7500, "top-right", props.theme, 1);
    }).finally(() => {
        setLoading(false);
    });
};

// Save Peppol info for a specific company
const savePeppolInfoForCompany = (companyData) => {
    SftGlobalData.post('peppol&action=save', {key: props.api, data: [companyData]}, {}).then(response => {
        props.notify(props.t('savedChanges'), 7500, "top-center", props.theme, 1);
        fetchData();
    }).catch(e => {
        console.log("Error", e);
        props.notify('Error saving Peppol information: ' + e.message, 7500, "top-center", props.theme, 1);
    });
};

const handleSubscribe = (company) => {
    setSelectedCompany(company);
    setShowForm(true);
};

// Return to overview list
const handleBackToList = () => {
    setShowForm(false);
    setSelectedCompany(null);
};

// Handle form submission
const handleFormSubmit = (formData) => {
    if (!formData.homepage.startsWith('https://')) {
        if (formData.homepage.startsWith('http://')) {
            formData.homepage = formData.homepage.replace('http://', 'https://');
        } else {
            formData.homepage = `https://${formData.homepage}`;
        }
    }
    createSupplier(formData);
};

function cleanBase64ForIbanity(base64Input) {
    if (!base64Input) {
        throw new Error('Base64 input is required');
    }
    
    let cleanedBase64 = base64Input;
    
    // Remove data URL prefix if present (e.g., "data:image/jpeg;base64,")
    if (cleanedBase64.includes(',')) {
        cleanedBase64 = cleanedBase64.split(',')[1];
    }
    
    // Remove all whitespace characters (spaces, newlines, tabs)
    cleanedBase64 = cleanedBase64.replace(/\s/g, '');
    
    // Add proper padding if needed
    const padding = (4 - cleanedBase64.length % 4) % 4;
    cleanedBase64 = cleanedBase64 + '='.repeat(padding);
    
    // Validate it's proper base64
    try {
        atob(cleanedBase64);
    } catch (e) {
        throw new Error('Invalid base64 format after cleaning');
    }
    
    return cleanedBase64;
}

const createSupplier = (formData) => {
    let url = 'https://peppol.softtouch.eu/createSupplier';
    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);
    
    let postBody = {
        "password": hash,
        "vat": formData.vat?.trim(),
        "email": formData.email?.trim(),
        "ibans": [
            {
                "value": formData.iban
            }
        ],
        "name": formData.corpname?.trim(),
        "city": formData.city?.trim(),
        "country": formData.country?.trim(),
        "homepage": formData.homepage?.trim(),
        "phoneNumber": formData.phoneNumber?.trim(),
        "street": formData.street?.trim(),
        "streetNumber": formData.streetNr?.trim(),
        "zip": formData.zip?.trim(),
        "representativeIdScan": cleanBase64ForIbanity(formData.idDocuments),
    };

    axios.post(url, postBody, {
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(async response => {
        if (SftGlobalData.debug) {
            console.log('url', url);
            console.log('data', postBody);
            console.log('response', response.data);
        }

        if(response.data?.errors && response.data?.errors?.length > 0){
            //get the first error and use code: detail
            props.notify(`Error creating supplier \n Detail: ${response.data.errors[0]?.detail} \n Pointer: ${response.data.errors[0]?.source?.pointer}`, 7500, "top-center", props.theme, 1);
            return;
        }

        //from the result we need to get the id of the supplier
        const supplierId = response.data.id;
        console.log(` supplierId:`, supplierId);
        
        //next we need to save the supplier id in the database (peppol_supplier_id and set peppol_consent to true)
        SftGlobalData.post('peppol&action=save', {
            key: props.api, data: [{
                id: selectedCompany.id,
                tva: formData.vat?.trim(),
                desc: formData.corpname?.trim(),
                address1: formData.street?.trim() + ' ' + formData.streetNr?.trim(),
                zipcode: formData.zip?.trim(),
                city: formData.city?.trim(),
                country: formData.countryCorp?.trim(),
                peppol_consent: true,
                peppol_supplier_id: supplierId
            }]
        }, {}).then(async response => {
            await activatePeppol();
            setFetchedCorpList(response.data);
            handleBackToList(); // Return to list view after successful creation
            fetchData(); // Refresh data
        }).catch(e => {
            console.log("Error", e);
        })
    }).catch(error => {
        if (SftGlobalData.debug) {
            console.log('url', url);
            console.log('data', postBody);
            console.log('error', error);
        }
        props.notify('Error creating supplier: ' + (error.response?.data?.message || error.message), 7500, "top-center", props.theme, 1);
        setLoading(false);
    });
};

const updateModule = (postData) => {
    // SftGlobalData.post('client&action=updateModules', postData, {}).then(response => {
    //     try{
    //         if(response.status === 200){
    SftGlobalData.post('client&action=createDeliveryNote', { key: props.api,isPeppol: true, user: props.username, sftUserId: props.userId }, {}).then(response => {
        
    });
    //         }
    //     }catch(e){
    //         console.log(e);
    //     }
    // });
}

const activatePeppol = () => {
    return new Promise((resolve, reject) => {
        updateModule({key: props.api, mod_id: 61, setActive: 1, isPeppol: true});
        resolve();
    });
}

const fetchData = () => {
    setLoading(true);
    SftGlobalData.post('peppol&action=fetch', {key: props.api}, {}).then(response => {
        setCorpList(response.data || []);
        setFetchedCorpList(response.data || []);
        // Fetch supplier data for registered corporations
        fetchSupplierDataForRegistered(response.data || []);
        // Fetch invoice errors for registered corporations (SFT users only)
        if (props.loginObj?.sftUserId !== "undefined" && props.loginObj?.sft_status != "0") {
            fetchInvoiceErrors(response.data || []);
        }
    }).catch(e => {
        console.log("Error", e);
    })
    .finally(() => {
        setLoading(false);
    });
};

// Fetch supplier data for all registered corporations to check names
const fetchSupplierDataForRegistered = async (corporations) => {
    const registeredCorps = corporations.filter(corp => corp.peppol_supplier_id);
    if (registeredCorps.length === 0) return;

    const currentYear = new Date().getFullYear();
    const salt = `peppol-${currentYear}`;
    const hash = crypto.SHA256(`${SftGlobalData.peppol_password}${salt}`).toString(crypto.enc.Hex);

    const supplierPromises = registeredCorps.map(corp =>
        axios.post('https://peppol.softtouch.eu/getSupplier', {
            password: hash,
            id: corp.peppol_supplier_id
        }, {
            headers: { 'Content-Type': 'application/json' }
        }).then(response => ({
            supplierId: corp.peppol_supplier_id,
            data: response.data
        })).catch(error => {
            if (SftGlobalData.debug) {
                console.log('Error fetching supplier:', corp.peppol_supplier_id, error);
            }
            return null;
        })
    );

    const results = await Promise.all(supplierPromises);
    const supplierMap = {};
    results.forEach(result => {
        if (result && result.data) {
            supplierMap[result.supplierId] = result.data;
        }
    });
    setSupplierData(supplierMap);
};

// Fetch invoice errors for all registered corporations (SFT users only)
const fetchInvoiceErrors = async (corporations) => {
    const registeredCorps = corporations.filter(corp => corp.peppol_supplier_id);
    if (registeredCorps.length === 0) {
        setInvoiceErrors({});
        return;
    }

    setLoadingErrors(true);

    let postData = {
        key: props.api
    };

    if(registeredCorps.find(corp => corp.default_b2b_corp === "1")){
        postData.useB2B = true;
    }

    try {
        const response = await SftGlobalData.post('peppol&action=invoiceErrors', postData, {});

        const allErrors = response.data || [];

        // Group errors by corp field and match to registered corporations
        const errorsMap = {};
        allErrors.forEach(error => {
            // Find the matching corporation by id
            const matchingCorp = registeredCorps.find(corp => String(corp.id) === String(error.corp));
            if (matchingCorp) {
                if (!errorsMap[matchingCorp.id]) {
                    errorsMap[matchingCorp.id] = {
                        corp: matchingCorp,
                        errors: []
                    };
                }
                errorsMap[matchingCorp.id].errors.push(error);
            }
        });

        setInvoiceErrors(errorsMap);
    } catch (e) {
        console.log("Error fetching invoice errors:", e);
        setInvoiceErrors({});
    } finally {
        setLoadingErrors(false);
    }
};

// Build a stable per-row key for selection state. Uses fields that are always
// present (corp + source table + docnr) so it stays unique per row even before the
// backend starts returning unique_id. The actual reset payload uses unique_id (below).
const getErrorKey = (error) => `${error.corp}|${error.source_table || 'faktuur'}|${error.docnr}`;

// Toggle selection of a single error row
const toggleErrorSelection = (key) => {
    setSelectedErrorKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        return next;
    });
};

// Toggle selection of all error rows for a given corp
const toggleSelectAllForCorp = (errors) => {
    const keys = errors.map(error => getErrorKey(error));
    setSelectedErrorKeys(prev => {
        const next = new Set(prev);
        const allSelected = keys.every(key => next.has(key));
        keys.forEach(key => allSelected ? next.delete(key) : next.add(key));
        return next;
    });
};

// Set the selected invoice errors back to "open" (peppol_status = 43501) in bulk.
// IDs are the true primary keys, so faktuur no longer needs corp scoping and b2b
// only needs to be split by source table (invno vs creditnoteno lives in different tables).
const handleResendSelected = async (targetCorpId) => {
    const faktuurIds = new Set();  // faktuur.id
    const b2bBySource = {};        // source_table -> Set of primary keys (invno / creditnoteno)

    Object.entries(invoiceErrors).forEach(([corpId, { errors }]) => {
        if (targetCorpId && String(corpId) !== String(targetCorpId)) return;
        errors.forEach(error => {
            if (!selectedErrorKeys.has(getErrorKey(error))) return;
            if (error.source_table) {
                // B2B invoice/creditnote
                if (!b2bBySource[error.source_table]) b2bBySource[error.source_table] = new Set();
                b2bBySource[error.source_table].add(error.unique_id);
            } else {
                // Faktuur
                faktuurIds.add(error.unique_id);
            }
        });
    });

    const requests = [];
    if (faktuurIds.size > 0) {
        requests.push({ key: props.api, ids: Array.from(faktuurIds), useB2B: false });
    }
    Object.entries(b2bBySource).forEach(([sourceTable, ids]) => {
        requests.push({ key: props.api, ids: Array.from(ids), useB2B: true, source_table: sourceTable });
    });

    if (requests.length === 0) return;

    setResendingErrors(true);
    try {
        await Promise.all(requests.map(body => SftGlobalData.post('peppol&action=resendInvoices', body, {})));
        props.notify(props.t('peppol_errors_reopened') || 'Selected invoices set to open', 7500, "top-right", props.theme, 1);
        setSelectedErrorKeys(new Set());
        fetchData();
    } catch (e) {
        if (SftGlobalData.debug) console.log("Error resending invoices:", e);
        props.notify(props.t('errorOccured'), 7500, "top-right", props.theme, 1);
    } finally {
        setResendingErrors(false);
    }
};

useEffect(() => {
    if (props.changeScreen) {
        props.changeScreen("licence");
    }

    if(props.api){
        fetchData();
    }
}, [props.api]);

// If showing the form, render the form instead of the table
if (showForm && selectedCompany) {
    const isEditing = selectedCompany.isEditing === true;
    
    return (
        <div className={(props.screenWidth) < 600 ? 'not_expanded container_content _90perc ' : 'expanded container_content _90perc '}>
            <Container maxWidth="lg" sx={{ my: 2 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {isEditing 
                            ? `${props.t('peppol_update_subscription_for')} ${selectedCompany.name}`
                            : `${props.t('peppol_subscription_for')} ${selectedCompany.name}`
                        }
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        {isEditing
                            ? props.t('peppol_update_subscription_info')
                            : props.t('peppol_subscription_info')
                        }
                    </Typography>
                    
                    <PeppolRegistrationForm 
                        initialData={selectedCompany}
                        onSubmit={isEditing ? handleUpdateSubmit : handleFormSubmit}
                        onCancel={handleBackToList}
                        t={props.t}
                        isEditing={isEditing}
                        setLoading={setLoading}
                        disabledFields={isEditing ? ['vat', 'idDocuments'] : []} // Disable fields that can't be patched
                    />
                    
                    <Button 
                        variant="outlined" 
                        onClick={handleBackToList}
                        sx={{ mt: 2 }}
                    >
                        {props.t('back_to_overview')}
                    </Button>
                </Paper>
            </Container>
        </div>
    );
}

// Otherwise, render the table view
return (
    <div className={(props.screenWidth) < 600 ? 'not_expanded container_content _80perc ' + props.theme + ' container_content_' + props.theme : 'expanded container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
        <div className="page-header">
            <h1>{props.t ? props.t('peppol_overview_title') : 'Peppol Overview'}</h1>
            <p>{props.t ? props.t('peppol_overview_description') : 'View and manage your Peppol-enabled corporations'}</p>
        </div>
        
        <PeppolDataTable 
            data={corpList}
            loading={loading}
            theme={props.theme}
            checkboxSelection={false} // No selection in overview
            showCheckVatAction={false} // No VAT check action in this overview
            customColumns={customColumns}
            rowMapper={customRowMapper}
            pagination={true}
            actionComponents={[]} // Empty array since we've defined actions in customColumns
            additionalProps={{
                autoHeight: true,
                density: "standard",
                disableSelectionOnClick: true,
                sx: { 
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: props.theme === 'dark' ? '#333' : '#f5f5f5'
                    }
                }
            }}
        />

        {/* Invoice Errors Section - SFT Users Only, hidden when no errors */}
        {(props.loginObj?.sftUserId !== "undefined" && props.loginObj?.sft_status != "0") && !loadingErrors && Object.keys(invoiceErrors).length > 0 && (
            <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ErrorOutlineIcon sx={{ color: Colors.error_red || '#f44336', mr: 1 }} />
                    <Typography variant="h6">
                        {props.t('peppol_invoice_errors') || 'Invoice Errors'}
                    </Typography>
                </Box>

                {Object.entries(invoiceErrors).map(([corpId, { corp, errors }]) => (
                        <Accordion key={corpId} defaultExpanded={false} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: props.theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
                                    '&:hover': { backgroundColor: props.theme === 'dark' ? '#333' : '#e8e8e8' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: Colors.sft_blue }}>
                                        {corp.name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        ({corp.vat})
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        backgroundColor: Colors.error_red || '#f44336',
                                        color: 'white',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1
                                    }}>
                                        {errors.length} {errors.length === 1 ? 'error' : 'errors'}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                {(() => {
                                    const selectedCount = errors.filter(error => selectedErrorKeys.has(getErrorKey(error))).length;
                                    const allSelected = errors.length > 0 && selectedCount === errors.length;
                                    const someSelected = selectedCount > 0 && selectedCount < errors.length;
                                    return (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                {selectedCount > 0 && (
                                                    <Typography variant="body2" color="textSecondary">
                                                        {selectedCount} {props.t('selected') || 'selected'}
                                                    </Typography>
                                                )}
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="primary"
                                                    disabled={selectedCount === 0 || resendingErrors}
                                                    onClick={() => handleResendSelected(corpId)}
                                                >
                                                    {props.t('peppol_set_to_open') || 'Set to open'}
                                                </Button>
                                            </Box>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell padding="checkbox">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={allSelected}
                                                                    indeterminate={someSelected}
                                                                    onChange={() => toggleSelectAllForCorp(errors)}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120 }}><strong>{props.t('docnr') || 'Document Nr'}</strong></TableCell>
                                                            <TableCell sx={{ width: 140 }}><strong>{props.t('invoice_type') || 'Type'}</strong></TableCell>
                                                            <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}><strong>{props.t('created_at') || 'Created At'}</strong></TableCell>
                                                            <TableCell><strong>{props.t('remark') || 'Remark'}</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {errors.map((error, index) => {
                                                            const errorKey = getErrorKey(error);
                                                            return (
                                                                <TableRow key={errorKey || index} selected={selectedErrorKeys.has(errorKey)} sx={{ '&:hover': { backgroundColor: props.theme === 'dark' ? '#333' : '#f5f5f5' } }}>
                                                                    <TableCell padding="checkbox">
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={selectedErrorKeys.has(errorKey)}
                                                                            onChange={() => toggleErrorSelection(errorKey)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell sx={{ width: 120 }}>{error.docnr}</TableCell>
                                                                    <TableCell sx={{ width: 140 }}>{error.invoice_type || '-'}</TableCell>
                                                                    <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>{error.error_log_created_at}</TableCell>
                                                                    <TableCell sx={{ color: Colors.error_red || '#f44336' }}>{error.remark}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    );
                                })()}
                            </AccordionDetails>
                        </Accordion>
                    ))}
            </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
        >
            <DialogTitle id="delete-dialog-title">
                {props.t ? props.t('delete_peppol_supplier') : 'Delete Peppol Supplier'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="delete-dialog-description">
                    {props.t ? 
                        props.t('delete_peppol_supplier_confirm', { name: companyToDelete?.name }) : 
                        `Are you sure you want to delete the Peppol configuration for ${companyToDelete?.name}? This action cannot be undone.`
                    }
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                    {props.t ? props.t('cancel') : 'Cancel'}
                </Button>
                <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                    {props.t ? props.t('delete') : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
        
        {/* Patch Confirmation Dialog */}
        <Dialog
            open={patchDialogOpen}
            onClose={() => setPatchDialogOpen(false)}
            aria-labelledby="patch-dialog-title"
            aria-describedby="patch-dialog-description"
        >
            <DialogTitle id="patch-dialog-title">
                {props.t ? props.t('patch_peppol_supplier') : 'Update Peppol Supplier'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="patch-dialog-description">
                    {props.t('patch_peppol_supplier_confirm')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPatchDialogOpen(false)} color="primary">
                    {props.t ? props.t('cancel') : 'Cancel'}
                </Button>
                <Button onClick={handlePatchConfirm} color="primary" autoFocus>
                    {props.t ? props.t('update') : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
        
        {
            (props.loginObj.sftUserId !== "undefined" && props.loginObj?.sft_status != "0") && <FloatingSyncButton disabled={corpList.filter((corp) => corp.peppol_supplier_id).length === 0} onClick={() => { if(SftGlobalData.confirmWindow(props.t('peppol_sync_confirm'))){startPeppolSync()}} } bottom={true} />
        }

        {/* Backsync (outbound status check) - SUPER users only for now */}
        {
            (props.loginObj?.sft_group === "SUPER") && <FloatingSyncButton disabled={corpList.filter((corp) => corp.peppol_supplier_id).length === 0} icon={<SyncAltIcon />} color={"primary"} title={props.t('peppol_backsync') || 'Backsync'} onClick={() => { if(SftGlobalData.confirmWindow(props.t('peppol_backsync_confirm'))){startPeppolBacksync()}} } bottom={false} />
        }
    </div>
);
}

export default PeppolOverview;